/**
 * The problem: when users zoom in/out, the pages gets resized and the current scroll point in the parent div is
 *   no longer showing the portion of the document the user was previously looking at. And we cannot simply shift
 *   scrollTop based on scale since the offset is not a simple delta based on difference in scale.
 *
 * This solution: on initial render, we inspect the page elements for values that are static -- e.g. document margins,
 *   parent component paddings, unscaled width/height of each page -- and store those values. On rescale, given
 *   scrollTop, scrollLeft and scale values, we can work out which page and at what point on that page the current view
 *   is at. Then, given a new scale, we can infer the expected scrollTop/scrollLeft to ensure that the top-left corner
 *   of the view references the same point after the rescale.
 */
import {
  getElementMargins,
  getElementPaddings,
  watchScroll,
} from "../../utils";
import { EventBus, Events } from "../eventBus";
import { CSSClass, ElemID } from "./constants";
import { getCurrentScale } from "./zoom";

let state;

const pageContainer = document.getElementById(ElemID.EDITOR_PAGE_CONTAINER);
const pageContainerWrapper = document.getElementById(
  ElemID.EDITOR_PAGE_CONTAINER_WRAPPER,
);
const previewPageContainerWrapper = document.getElementById(
  ElemID.PREVIEW_PAGE_CONTAINER_WRAPPER,
);

export function initWayfinder() {
  const firstPageElem = pageContainer.querySelector("." + CSSClass.PAGE);
  let maxPageWidth = 0;

  const pages = [];
  pageContainer.querySelectorAll("." + CSSClass.PAGE).forEach(function (page) {
    pages.push({
      id: page.id,
      width: Number(page.dataset.unscaledWidth),
      height: Number(page.dataset.unscaledHeight),
    });
    maxPageWidth = Math.max(maxPageWidth, Number(page.dataset.unscaledWidth));
  });

  const containerPadding = getElementPaddings(pageContainer);
  const pageMargins = getElementMargins(firstPageElem);

  state = {
    spaceLeft: containerPadding.left + pageMargins.left, // margin between left of page and left of container
    spaceRight: containerPadding.right + pageMargins.right, // margin between right of page and right of container
    spaceTop: containerPadding.top + pageMargins.top, // margin between top of first page and top of container
    spaceBottom: containerPadding.bottom + pageMargins.bottom, // margin between last page and bottom of container
    spaceBetweenPages: pageMargins.top + pageMargins.bottom, // margin between pages
    maxPageWidth: maxPageWidth, // unscaled width of widest page
    pages: pages,
  };
}

export function wayfinderGetSizingData() {
  return {
    maxPageWidth: state.maxPageWidth,
    spaceLeft: state.spaceLeft,
    spaceRight: state.spaceRight,
    spaceTop: state.spaceTop,
    spaceBottom: state.spaceBottom,
    spaceBetweenPages: state.spaceBetweenPages,
  };
}

export function wayfinderTrackActivePage(isPreview) {
  const screen = isPreview ? "preview" : "editor";
  const container = isPreview
    ? previewPageContainerWrapper
    : pageContainerWrapper;
  return watchScroll(container, function (scrollState) {
    const verticalOffset = findVerticalPageOffset(
      scrollState.scrollTop,
      getCurrentScale(screen),
    );
    let page = verticalOffset.pageNum;
    if (verticalOffset.offsetPercent > 0.9 && page + 1 < state.pages.length) {
      page += 1;
    }
    EventBus.emit(Events.PAGE_IN_VIEW, {
      page: page + 1, // +1 because wayfinder uses 0-index
      screen: isPreview ? "preview" : "editor",
    });
  });
}

export function wayfinderGetPageOffset(pageIndex, scale) {
  let top = state.spaceTop;
  for (let i = 0; i < pageIndex; i++) {
    let pageHeight = state.pages[i].height * scale;
    top += state.spaceBetweenPages + pageHeight;
  }
  return {
    left: state.spaceLeft,
    top: top,
  };
}

export function wayfinderGetCurrentLocation(scale, screen) {
  const container =
    screen === "preview" ? previewPageContainerWrapper : pageContainerWrapper;
  const verticalOffset = findVerticalPageOffset(container.scrollTop, scale);
  const horizontalOffset = findHorizontalPageOffset(
    container.scrollLeft,
    scale,
  );
  return {
    // identify where left view boundary intersects with the page
    horizontalOffsetPx: horizontalOffset.unscaledPx, // unscaled pixel offset (null if not on page)
    pageNum: verticalOffset.pageNum, // page number, 0-indexed (null if not on page)
    pageOffsetPercent: verticalOffset.offsetPercent, // % down the page (null if not on page)
  };
}

export function wayfinderScrollToLocation(location, scale, screen) {
  const container =
    screen === "preview" ? previewPageContainerWrapper : pageContainerWrapper;

  const newScrollTop = mapVerticalPageOffsetToScrollTop(
    location.pageNum,
    location.pageOffsetPercent,
    scale,
  );

  const newScrollLeft = mapHorizontalPageOffsetToScrollLeft(
    location.horizontalOffsetPx,
    scale,
  );

  if (newScrollTop !== null) {
    container.scrollTop = newScrollTop;
  }
  if (newScrollLeft !== null) {
    container.scrollLeft = newScrollLeft;
  }
}

export function wayfinderGetClosestPage(targetElement, scale) {
  /** identifies the page that has closest horizontal distance to the given element and returns its id **/

  // reval this each time since user could have resized page
  const containerRect = pageContainerWrapper.getBoundingClientRect();
  const containerHeight = containerRect.bottom - containerRect.top;

  // blacklisted zones at top and bottom where we exclude pages.
  // This prevents matching element to a page that is mostly hidden with only a little bit showing
  let blackZoneTop = Math.max(state.spaceBetweenPages, state.spaceTop) + 30;
  let blackZoneBottom =
    Math.max(state.spaceBetweenPages, state.spaceBottom) + 30;
  // unless screen is too small, then when don't bother with blackouts.
  if (blackZoneTop + blackZoneBottom > containerHeight) {
    blackZoneBottom = 0;
    blackZoneTop = 0;
  }

  // find y position within container that is not within blackout zone that is closest to element top
  const rect = targetElement.getBoundingClientRect();
  const containerY = Math.min(
    Math.max(rect.top - containerRect.top, blackZoneTop),
    containerRect.bottom - blackZoneBottom,
  );

  // get y position in virtual container that matches container-y
  const y = containerY + pageContainerWrapper.scrollTop;

  // if y ends up in a margin between pages, we match downwards if top half of view, else match upwards
  const matchDownwards = y < containerHeight / 2;

  const pageOffset = findVerticalPageOffset(y, scale, matchDownwards);
  if (pageOffset.pageNum) {
    return state.pages[pageOffset.pageNum].id;
  } else {
    // if we somehow ended up before first page or after last page (e.g. if screen height is tiny)
    if (pageOffset.bottom) {
      return state.pages[state.pages.length - 1].id;
    } else {
      return state.pages[0].id;
    }
  }
}

function findVerticalPageOffset(scrollTop, scale, matchDownwards = true) {
  if (scrollTop <= state.spaceTop) {
    return { pageNum: null, offsetPercent: null };
  }

  // TODO: optimise using bisect call e.g. https://github.com/d3/d3-array/blob/main/src/bisector.js#L22

  let remaining = scrollTop - state.spaceTop;
  for (let i = 0; i < state.pages.length; i++) {
    let page = state.pages[i];
    let scaledHeight = page.height * scale;

    if (remaining < scaledHeight) {
      // if in middle of page
      return { pageNum: i, offsetPercent: remaining / scaledHeight };
    }
    remaining -= scaledHeight;

    if (remaining < state.spaceBetweenPages) {
      // in margins between pages
      if (matchDownwards) {
        return { pageNum: i + 1, offsetPercent: 0 }; // scroll to beginning of next page
      } else {
        return { pageNum: i, offsetPercent: 1 }; // reference end of page before gap
      }
    }
    remaining -= state.spaceBetweenPages;
  }
  // should not reach here, but just in case, we don't impact scroll.
  return { pageNum: null, offsetPercent: null, bottom: true };
}

function findHorizontalPageOffset(scrollLeft, scale) {
  if (scrollLeft <= state.spaceLeft) {
    return { unscaledPx: null }; // null means left boundary not within page so don't scroll
  }

  const pageOffset = scrollLeft - state.spaceLeft;
  return { unscaledPx: pageOffset / scale };
}

function mapVerticalPageOffsetToScrollTop(pageNum, offsetPercent, scale) {
  if (pageNum === null) {
    return null;
  }

  let scrollTop = state.spaceTop;

  // move cursor to end of prev page
  for (let i = 0; i < pageNum; i++) {
    let page = state.pages[i];
    let scaledHeight = page.height * scale;
    scrollTop += scaledHeight + state.spaceBetweenPages;
  }

  // now apply the offset to % of scrolled page
  let page = state.pages[pageNum];
  let scaledHeight = page.height * scale;
  scrollTop += scaledHeight * offsetPercent;

  return scrollTop;
}

function mapHorizontalPageOffsetToScrollLeft(unscaledPx, scale) {
  return unscaledPx !== null ? state.spaceLeft + unscaledPx * scale : null;
}
